import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsIcon from '@mui/icons-material/Notifications';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import { useMemo, useState } from 'react';
import { ListItemAvatar, ListItemIcon, List, ListItemText, ListItem, Button, Menu, MenuItem, Box, Typography, Divider } from '@mui/material';
import { useAlerts, useUpdateAlertSnooze } from '../../hooks/useAlerts';
import dayjs from 'dayjs';
import { useSimpleAlert } from '../../hooks/useSimpleAlert';
import { useNavigate } from 'react-router';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const snoozeOptions = [
    { value: 0.5, label: '30 Minutes' },
    { value: 1, label: '1 Hour' },
    { value: 2, label: '2 Hours' },
    { value: 3, label: '3 Hours' },
    { value: 4, label: '4 Hours' },
    { value: 6, label: '6 Hours' },
    { value: 8, label: '8 Hours' },
    { value: 12, label: '12 Hours' },
];

export const AlertsList = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { data: alerts = [] } = useAlerts();
    const { mutate: updateAlertSnooze } = useUpdateAlertSnooze();
    const { showSimpleAlert } = useSimpleAlert();
    const navigate = useNavigate();

    const handleSnoozeOptions = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSnooze = (_id: string, hours: number | null) => {
        const snooze = hours ? dayjs().add(hours, 'hour').toDate() : undefined;

        setAnchorEl(null);

        updateAlertSnooze(
            { id: _id.toString(), data: { snooze } },
            {
                onSuccess: () => showSimpleAlert({ title: 'Alert successfully updated' }),
                onError: () => showSimpleAlert({ title: 'Error updating Alert', severity: 'error' }),
            },
        );
    };

    const handleEdit = (id: string) => navigate(`/alerts/${id}`);
    const handleCreate = () => navigate('/alerts/create');

    const activeAlerts = useMemo(() => alerts.filter((alert) => alert.active), [alerts]);
    const inactiveAlerts = useMemo(() => alerts.filter((alert) => !alert.active), [alerts]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100%',
                height: '100%',
            }}>
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
                    <Typography variant="h5">Alerts</Typography>
                    <Button variant="contained" onClick={handleCreate}>
                        Create Alert
                    </Button>
                </Box>
                <Divider />
            </Box>
            <List sx={{ width: '100%', height: '100%', overflowY: 'scroll', p: 0 }}>
                {activeAlerts.length > 0 && (
                    <ListItem divider sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                        <ListItemText primary="Active Alerts" sx={{ textAlign: 'center' }} />
                    </ListItem>
                )}
                {activeAlerts.map((alert) => {
                    const { _id, description, station, snooze, triggered, updatedAt } = alert;
                    const id = _id.toString();
                    const buttonId = `button_${id}`;

                    let status;
                    let statusDescription = '';

                    if (snooze) {
                        status = 'Snoozed';
                        statusDescription = `Snoozed until ${dayjs(snooze).format('llll')}`;
                    } else if (triggered) {
                        status = 'Triggered';
                        statusDescription = `Triggered ${dayjs(updatedAt).format('llll')}`;
                    } else {
                        status = 'Idle';
                        statusDescription = `Checked ${dayjs(updatedAt).format('llll')}`;
                    }

                    return (
                        <ListItem key={id} id={id} divider>
                            <ListItemAvatar>
                                <ListItemIcon>
                                    {snooze && <NotificationsPausedIcon color="warning" fontSize="large" />}
                                    {!snooze && triggered && <NotificationsActiveIcon color="error" fontSize="large" />}
                                    {!snooze && !triggered && <NotificationsIcon color="primary" fontSize="large" />}
                                </ListItemIcon>
                            </ListItemAvatar>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <ListItemText primary={`Station: ${station.name}`} secondary={`${description}`} />
                                <ListItemText primary={`Status: ${status}`} secondary={`${statusDescription}`} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    {!snooze && (
                                        <Button disabled={!triggered} id={buttonId} disableElevation variant="contained" endIcon={<KeyboardArrowDownIcon />} onClick={handleSnoozeOptions}>
                                            Snooze Options
                                        </Button>
                                    )}
                                    {snooze && (
                                        <Button id={buttonId} disableElevation variant="outlined" onClick={() => handleSnooze(id, null)}>
                                            Cancel Snooze
                                        </Button>
                                    )}
                                    <Menu anchorEl={anchorEl} open={anchorEl?.id === buttonId} onClose={() => setAnchorEl(null)}>
                                        {snoozeOptions.map(({ value, label }) => {
                                            return (
                                                <MenuItem key={value} disableRipple onClick={() => handleSnooze(id, value)}>
                                                    {label}
                                                </MenuItem>
                                            );
                                        })}
                                    </Menu>
                                    <Button id={buttonId} disableElevation variant="contained" onClick={() => handleEdit(id)} sx={{ ml: 2 }}>
                                        Edit
                                    </Button>
                                </Box>
                            </Box>
                        </ListItem>
                    );
                })}
                {inactiveAlerts.length > 0 && (
                    <ListItem divider sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                        <ListItemText primary="Inactive Alerts" sx={{ textAlign: 'center' }} />
                    </ListItem>
                )}
                {inactiveAlerts.map((alert) => {
                    const { _id, description, station } = alert;
                    const id = _id.toString();
                    const buttonId = `button_${id}`;

                    return (
                        <ListItem key={id} id={id} divider>
                            <ListItemAvatar>
                                <ListItemIcon>
                                    <NotificationsOffIcon fontSize="large" />
                                </ListItemIcon>
                            </ListItemAvatar>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
                                    <ListItemText primary={`Station: ${station.name}`} secondary={`${description}`} />
                                    <Button id={buttonId} disableElevation variant="contained" onClick={() => handleEdit(id)}>
                                        Edit
                                    </Button>
                                </Box>
                            </Box>
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};
